import React, { useState, useEffect } from 'react';
import { shape, string } from 'prop-types';
import axios from 'axios';

import BasePage from '../BasePage/BasePage';
import { getURLParamByName } from '../../../shared-with-server/product-name-utils';
import { composeDestinationQuerystring } from '../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../destructured-front-end-config';
import { safeDecodeJwt } from '../../utils/jwt-utils';
import {
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  LoadingSubtext,
  PageHeader,
  PageTitle,
  PageSubtitle,
} from './RegistrationEntryPageStyles';
import EmailVerificationPanel from './Panels/EmailVerificationPanel';

const RegistrationEntryPage = ({ brand, destPath }) => {
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const redirectToLogin = (retURL) => {
    const params = new URLSearchParams();
    if (retURL) {
      params.append('retURL', retURL);
    }
    const queryString = params.toString();
    const loginUrl = queryString ? `/login-v2?${queryString}` : '/login-v2';
    window.location.href = loginUrl;
  };

  const redirectToRegistrationStep = (step) => {
    const registrationUrl = `/uam/step${step}?${composeDestinationQuerystring(destinationInfo, destPath)}`;
    window.location.href = registrationUrl;
  };

  const handleGoToLogin = () => {
    const retURL = getURLParamByName(location?.search, 'retURL');
    redirectToLogin(retURL);
  };

  const processRegistrationEntry = async (email, retURL) => {
    try {
      setShowEmailVerification(false);

      // Build API URL with retURL parameter
      const apiUrl = `/api/v2/fi/multi-step-registration-entry${
        retURL ? `?retURL=${encodeURIComponent(retURL)}` : ''
      }`;

      // Make API call with email in body and retURL as query param
      const response = await axios.post(apiUrl, {
        email: email.trim(),
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        timeout: 10000,
      });

      // Handle successful response
      if (response.status === 200) {
        const { status: responseStatus, step } = response.data;

        if (responseStatus === 'new_registration') {
          // New user, redirect to first registration step
          redirectToRegistrationStep(1);
        } else if (responseStatus === 'existing_registration') {
          if (step === -1) {
            // Email verification required - show verification message
            setShowEmailVerification(true);
          } else {
            // Continue registration from specific step
            redirectToRegistrationStep(step);
          }
        } else if (responseStatus === 'completed_registration') {
          // User already has complete account, redirect to login
          redirectToLogin(retURL);
        } else {
          // Unknown/error status, redirect to login
          redirectToLogin(retURL);
        }
      } else {
        // Per TIE-3651 requirements: non-200 response, redirect to login
        redirectToLogin(retURL);
      }
    } catch (error) {
      console.error('Error processing registration entry:', error);

      const currentRetURL = getURLParamByName(location?.search, 'retURL');

      // Per TIE-3651 requirements: redirect to login on any API error
      redirectToLogin(currentRetURL);
    }
  };

  // Process registration entry on component mount
  useEffect(() => {
    const registrationToken = getURLParamByName(location?.search, 'registration_token');
    const retURL = getURLParamByName(location?.search, 'retURL');

    if (!registrationToken) {
      // If no token, redirect to login
      redirectToLogin(retURL);
      return;
    }

    // Decode JWT token to extract email
    const decodedToken = safeDecodeJwt(registrationToken);
    if (!decodedToken || !decodedToken.email) {
      // Invalid token, redirect to login
      redirectToLogin(retURL);
      return;
    }

    // Set the registration token cookie before API call
    const isSecure = location.protocol === 'https:';
    document.cookie = `registration_token=${registrationToken}; path=/; max-age=3600; SameSite=Lax${isSecure ? '; secure' : ''}`;

    // Store email for use in API call
    setUserEmail(decodedToken.email);

    // Auto-trigger registration entry process
    processRegistrationEntry(decodedToken.email, retURL);
  }, [processRegistrationEntry]);



  // Show email verification message
  if (showEmailVerification) {
    return (
      <BasePage brand={brand} destPath={destPath}>
        <PageHeader>
          <PageTitle>Email Verification Required</PageTitle>
          <PageSubtitle>
            Please check your email to continue registration.
          </PageSubtitle>
        </PageHeader>
        <EmailVerificationPanel
          userEmail={userEmail}
          onGoToLogin={handleGoToLogin}
        />
      </BasePage>
    );
  }

  // Show loading state while processing
  return (
    <BasePage brand={brand} destPath={destPath}>
        <PageHeader>
          <PageTitle>Processing Registration</PageTitle>
          <PageSubtitle>
            Please wait while we process your registration request.
          </PageSubtitle>
        </PageHeader>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Processing your registration</LoadingText>
          <LoadingSubtext>
            We&apos;re determining your registration status.
          </LoadingSubtext>
        </LoadingContainer>
      </BasePage>
  );
};

RegistrationEntryPage.propTypes = {
  brand: shape({
    short: string.isRequired,
    full: string.isRequired,
  }).isRequired,
  destPath: shape({
    dest: string,
    path: string,
    retURL: string,
  }),
};

RegistrationEntryPage.defaultProps = {
  destPath: {
    retURL: null,
  },
};

export default RegistrationEntryPage;
